import { create } from 'zustand'
import type { VideoRatio, VideoResolution } from '@/lib/constants'
import type { FrameImage, ApplyableSettings } from '@/types/generation'
import {
  parseTemplateParameters,
  resolveTemplate,
  extractDefaultValues,
} from '@/lib/template-parameters'
import {
  normalizeVideoDuration,
  normalizeVideoResolution,
} from '@/lib/video-settings'

interface SelectedTemplate {
  id: string
  name: string
  description: string | null
  thumbnailUrl: string | null
}

interface TemplateParameterState {
  templateRawPrompt: string | null
  templateParameters: Record<string, string> | null
}

interface GeneratorState extends TemplateParameterState {
  prompt: string
  ratio: VideoRatio
  resolution: VideoResolution
  duration: number
  frameImages: { firstFrame: FrameImage | null; lastFrame: FrameImage | null }
  isSubmitting: boolean
  lastPrompt: string
  selectedTemplate: SelectedTemplate | null
  usageLimitModalOpen: boolean
  ssoTokenExpiredModalOpen: boolean

  setPrompt: (prompt: string) => void
  setTemplateParameter: (key: string, value: string) => void
  setRatio: (ratio: VideoRatio) => void
  setResolution: (resolution: VideoResolution) => void
  setDuration: (duration: number) => void
  setFirstFrame: (frame: FrameImage | null) => void
  setLastFrame: (frame: FrameImage | null) => void
  setIsSubmitting: (isSubmitting: boolean) => void
  setLastPrompt: (prompt: string) => void
  reset: () => void
  setSelectedTemplate: (template: SelectedTemplate | null) => void
  clearTemplate: () => void
  applySettings: (settings: ApplyableSettings) => void
  setUsageLimitModalOpen: (open: boolean) => void
  setSsoTokenExpiredModalOpen: (open: boolean) => void
}

const initialState = {
  prompt: '',
  ratio: '16:9' as VideoRatio,
  resolution: '768P' as VideoResolution,
  duration: 5,
  frameImages: { firstFrame: null, lastFrame: null } as { firstFrame: FrameImage | null; lastFrame: FrameImage | null },
  isSubmitting: false,
  lastPrompt: '',
  selectedTemplate: null as SelectedTemplate | null,
  templateRawPrompt: null as string | null,
  templateParameters: null as Record<string, string> | null,
  usageLimitModalOpen: false,
  ssoTokenExpiredModalOpen: false,
}

export const useGeneratorStore = create<GeneratorState>((set) => ({
  ...initialState,

  setPrompt: (prompt) =>
    set({
      prompt,
      templateRawPrompt: null,
      templateParameters: null,
    }),

  setTemplateParameter: (key, value) =>
    set((state) => {
      if (!state.templateRawPrompt || !state.templateParameters) {
        return state
      }
      const newParams = { ...state.templateParameters, [key]: value }
      const newPrompt = resolveTemplate(state.templateRawPrompt, newParams)
      return {
        templateParameters: newParams,
        prompt: newPrompt,
      }
    }),

  setRatio: (ratio) => set({ ratio }),
  setResolution: (resolution) => set({ resolution }),
  setDuration: (duration) => set({ duration }),

  setFirstFrame: (frame) =>
    set((state) => ({
      frameImages: {
        ...state.frameImages,
        firstFrame: frame,
        lastFrame: frame ? state.frameImages.lastFrame : null,
      },
    })),

  setLastFrame: (frame) =>
    set((state) => ({
      frameImages: { ...state.frameImages, lastFrame: frame },
    })),

  setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
  setLastPrompt: (lastPrompt) => set({ lastPrompt }),

  reset: () => set(initialState),

  setSelectedTemplate: (template) => set({ selectedTemplate: template }),

  clearTemplate: () =>
    set({
      selectedTemplate: null,
      templateRawPrompt: null,
      templateParameters: null,
    }),

  applySettings: (settings) =>
    set((state) => {
      const params = parseTemplateParameters(settings.prompt)
      const hasParams = params.length > 0

      // 파라미터가 있으면 전달된 값(없으면 기본값)으로 프롬프트 치환
      const parameterValues = hasParams
        ? { ...extractDefaultValues(params), ...settings.templateParameters }
        : null
      const resolvedPrompt = parameterValues
        ? resolveTemplate(settings.prompt, parameterValues)
        : settings.prompt

      // Convert frameImages array to firstFrame/lastFrame
      let firstFrame: FrameImage | null = null
      let lastFrame: FrameImage | null = null
      if (settings.frameImages) {
        for (const img of settings.frameImages) {
          if (img.role === 'first_frame') firstFrame = img
          if (img.role === 'last_frame') lastFrame = img
        }
      }

      return {
        prompt: resolvedPrompt,
        ratio: settings.ratio ?? state.ratio,
        resolution: settings.resolution === undefined
          ? state.resolution
          : normalizeVideoResolution(settings.resolution),
        duration: settings.duration === undefined
          ? state.duration
          : normalizeVideoDuration(settings.duration),
        frameImages: { firstFrame, lastFrame },
        selectedTemplate: settings.template ?? null,
        templateRawPrompt: hasParams ? settings.prompt : null,
        templateParameters: parameterValues,
      }
    }),

  setUsageLimitModalOpen: (open) => set({ usageLimitModalOpen: open }),
  setSsoTokenExpiredModalOpen: (open) => set({ ssoTokenExpiredModalOpen: open }),
}))
